import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { DeductionDecision } from '@/features/admin-v2/claim-status';
import { api, isApiError } from '@/lib/api';

export const PANEL_RECEIVABLE_STATES = [
  'check',
  'send',
  'waiting',
  'match',
  'closed',
] as const;

export type PanelReceivableState = (typeof PANEL_RECEIVABLE_STATES)[number];
export type AgeingBucket = '0_30' | '31_60' | '61_90' | '90_plus';

export interface PanelReceivableAllocation {
  id: string;
  claimId: string;
  paidSen: number;
  deductionSen: number;
  deductionReason: string | null;
  decision: string | null;
}

export interface PanelReceivableRemittance {
  id: string;
  reference: string;
  status: string;
  amountSen: number;
  allocatedSen: number;
  receivedOn: string;
  allocations: PanelReceivableAllocation[];
}

/**
 * The receivables endpoint is an aggregate read model. `batchStatus`, claim
 * statuses and remittance statuses remain the source of truth; the five board
 * columns are derived by the client and are never persisted independently.
 * Financial totals are in sen.
 */
export interface PanelReceivableBatch {
  id: string;
  reference: string;
  panelId: string;
  panelName: string;
  branchName: string;
  periodLabel: string;
  batchStatus: string;
  claimStatuses: string[];
  remittances: PanelReceivableRemittance[];
  patientCount: number;
  totalClaimedSen: number;
  remittanceTotal: number;
  allocatedTotal: number;
  outstandingTotal: number;
  variance: number;
  submittedAt: string | null;
  dueAt: string | null;
  oldestOutstandingAt: string;
  ageingDays: number;
  ageingBucket: AgeingBucket;
  lastFollowUpAt: string | null;
  updatedAt: string;
  /** Board column derived by the API (RL-1086); preferred when present. */
  state?: PanelReceivableState;
}

export interface PanelReceivableClaim {
  id: string;
  claimReference: string;
  patientId: string;
  patientName: string;
  visitDate: string;
  status: string;
  claimedAmountSen: number;
  approvedAmountSen: number | null;
  receivedAmountSen: number;
  outstandingSen: number;
}

export interface PanelReceivableFollowUp {
  id: string;
  batchId: string;
  date: string;
  channel: string;
  note: string;
  nextDate: string | null;
  createdAt: string;
  createdByName: string;
}

export interface PanelReceivableBatchDetail extends PanelReceivableBatch {
  claims: PanelReceivableClaim[];
  followUps: PanelReceivableFollowUp[];
}

export interface AddPanelReceivableFollowUpInput {
  batchId: string;
  date: string;
  channel: string;
  note: string;
  nextDate: string | null;
}

export interface DecidePanelReceivableAllocationInput {
  batchId: string;
  allocationId: string;
  /** RL-1778: the Knock-off words; `write_off` is what `accept_deduction` was. */
  decision: DeductionDecision;
  reason?: string;
}

const READY_CLAIM_STATUSES = new Set(['submitted', 'approved', 'paid']);
/** Same as the API: an accepted deduction or a shortfall billed to the patient settles a claim. */
const SETTLED_CLAIM_STATUSES = new Set([
  'paid',
  'written_off',
  'billed_patient',
]);
const SENT_BATCH_STATUSES = new Set(['issued', 'sent', 'submitted']);

/** Derive the board stage from the underlying finance records. */
export function derivePanelReceivableState(
  batch: PanelReceivableBatch,
): PanelReceivableState {
  if (batch.state) return batch.state;
  const hasRemittance = batch.remittances.length > 0;
  const remittancesApplied =
    hasRemittance &&
    batch.remittances.every((remittance) => remittance.status === 'applied');
  const claimsSettled =
    batch.claimStatuses.length > 0 &&
    batch.claimStatuses.every((status) => SETTLED_CLAIM_STATUSES.has(status));

  if (
    batch.variance === 0 &&
    batch.outstandingTotal === 0 &&
    remittancesApplied &&
    claimsSettled
  )
    return 'closed';
  if (hasRemittance) return 'match';
  if (batch.submittedAt !== null || SENT_BATCH_STATUSES.has(batch.batchStatus))
    return 'waiting';
  if (
    batch.claimStatuses.length > 0 &&
    batch.claimStatuses.every((status) => READY_CLAIM_STATUSES.has(status))
  )
    return 'send';
  return 'check';
}

export const panelReceivableKeys = {
  all: ['panel-receivables'] as const,
  batches: ['panel-receivables', 'batches'] as const,
  detail: (id: string | undefined) =>
    ['panel-receivables', 'batches', id] as const,
};

function refreshReceivables(queryClient: QueryClient, batchId: string) {
  void queryClient.invalidateQueries({
    queryKey: panelReceivableKeys.batches,
  });
  void queryClient.invalidateQueries({
    queryKey: panelReceivableKeys.detail(batchId),
  });
}

function refreshInvalidTransition(
  queryClient: QueryClient,
  batchId: string,
  error: unknown,
) {
  if (
    isApiError(error) &&
    error.code === 'INVALID_PANEL_RECEIVABLE_TRANSITION'
  ) {
    return Promise.all([
      queryClient.refetchQueries({ queryKey: panelReceivableKeys.batches }),
      queryClient.refetchQueries({
        queryKey: panelReceivableKeys.detail(batchId),
      }),
    ]);
  }
  return undefined;
}

export function usePanelReceivableBatches() {
  return useQuery({
    queryKey: panelReceivableKeys.batches,
    queryFn: ({ signal }) =>
      api
        .get<PanelReceivableBatch[]>('/finance/panel-receivables/batches', {
          signal,
        })
        .then((response) => response.data),
  });
}

export function usePanelReceivableBatch(id: string | undefined) {
  return useQuery({
    queryKey: panelReceivableKeys.detail(id),
    queryFn: ({ signal }) =>
      api
        .get<PanelReceivableBatchDetail>(
          `/finance/panel-receivables/batches/${id}`,
          { signal },
        )
        .then((response) => response.data),
    enabled: !!id,
  });
}

export function useSendPanelReceivableBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      api
        .post(`/finance/invoices/${batchId}/issue`, { method: 'portal' })
        .then(() => batchId),
    onSuccess: (batchId) => refreshReceivables(queryClient, batchId),
    onError: (error, batchId) =>
      refreshInvalidTransition(queryClient, batchId, error),
  });
}

export function useAutoMatchPanelReceivable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchId,
      remittanceIds,
    }: {
      batchId: string;
      remittanceIds: string[];
    }) =>
      Promise.all(
        remittanceIds.map((remittanceId) =>
          api.post(`/finance/remittances/${remittanceId}/auto-match`),
        ),
      ).then(() => batchId),
    onSuccess: (batchId) => refreshReceivables(queryClient, batchId),
    onError: (error, variables) =>
      refreshInvalidTransition(queryClient, variables.batchId, error),
  });
}

export function useDecidePanelReceivableAllocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchId,
      allocationId,
      decision,
      reason,
    }: DecidePanelReceivableAllocationInput) =>
      api
        .post(`/finance/allocations/${allocationId}/decision`, {
          decision,
          reason,
        })
        .then(() => batchId),
    onSuccess: (batchId) => refreshReceivables(queryClient, batchId),
    onError: (error, variables) =>
      refreshInvalidTransition(queryClient, variables.batchId, error),
  });
}

export function useKnockOffPanelReceivable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchId,
      remittanceId,
    }: {
      batchId: string;
      remittanceId: string;
    }) =>
      api
        .post(`/finance/remittances/${remittanceId}/knock-off`)
        .then(() => batchId),
    onSuccess: (batchId) => refreshReceivables(queryClient, batchId),
    onError: (error, variables) =>
      refreshInvalidTransition(queryClient, variables.batchId, error),
  });
}

export function useAddPanelReceivableFollowUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchId,
      date,
      channel,
      note,
      nextDate,
    }: AddPanelReceivableFollowUpInput) =>
      api
        .post<PanelReceivableFollowUp>(
          `/finance/panel-receivables/batches/${batchId}/follow-ups`,
          { date, channel, note, nextDate },
        )
        .then((response) => response.data),
    onSuccess: (followUp) => refreshReceivables(queryClient, followUp.batchId),
  });
}
